import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CertifiedBadge } from "@/components/CertifiedBadge";
import OwnerEmailSetting from "@/components/OwnerEmailSetting";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, Users, Mail, FileText, BarChart3, Sparkles, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [certs, setCerts] = useState([]);
  const [stats, setStats] = useState({});
  const [notesMap, setNotesMap] = useState({});
  const [founderTest, setFounderTest] = useState(null);
  const [founderTestBusy, setFounderTestBusy] = useState(false);

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([api.get("/certifications"), api.get("/admin/stats")]);
    setCerts(c.data); setStats(s.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    try {
      await api.post(`/certifications/${id}/decision`, { decision, notes: notesMap[id] || "" });
      toast.success(`Certification ${decision}`);
      await load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const createFounderTest = async () => {
    setFounderTestBusy(true);
    try {
      const { data } = await api.post("/admin/founder-test-checkout");
      setFounderTest(data);
      toast.success(`$1 ${data.mode.toUpperCase()} checkout ready`);
    } catch (e) {
      toast.error(fmtErr(e) || "Could not create test checkout");
    } finally {
      setFounderTestBusy(false);
    }
  };

  const pending = certs.filter((c) => c.status === "pending");
  const decided = certs.filter((c) => c.status !== "pending");

  return (
    <div className="scent-wave min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.28em] uppercase text-emerald-700 font-semibold">Admin Console</div>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight text-[#0A1128]">Certification Review</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="rounded-full"
              data-testid="admin-digest-send"
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/digest/send");
                  toast.success(`Weekly digest sent to ${data.sent_to}`);
                } catch (e) { toast.error(fmtErr(e)); }
              }}
            >
              <FileText className="w-4 h-4 mr-1" /> Send weekly digest
            </Button>
            <Button
              variant="outline"
              className="rounded-full"
              data-testid="admin-test-email"
              onClick={async () => {
                try {
                  const r = await api.post("/admin/test-email");
                  toast.success(`Test email sent to ${r.data?.sent_to || "owner"}`);
                } catch (e) { toast.error(fmtErr(e)); }
              }}
            >
              <Mail className="w-4 h-4 mr-1" /> Send test email
            </Button>
            <Link to="/admin/insights" data-testid="admin-insights-link">
              <Button variant="outline" className="rounded-full">
                <BarChart3 className="w-4 h-4 mr-1" /> Insights
              </Button>
            </Link>
            <Link to="/admin/waitlist" data-testid="admin-waitlist-link">
              <Button variant="outline" className="rounded-full">
                <Users className="w-4 h-4 mr-1" /> Verify™ Waitlist
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { l: "Users", v: stats.users || 0 },
            { l: "Properties", v: stats.properties || 0 },
            { l: "Certified", v: stats.certified || 0 },
            { l: "Pending", v: stats.pending_certs || 0 },
          ].map((s) => (
            <div key={s.l} className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500">{s.l}</div>
              <div className="mt-2 font-serif text-4xl text-[#0A1128]">{s.v}</div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <OwnerEmailSetting />
        </div>

        {/* Founder $1 live-payment smoke test */}
        <div className="mt-8 rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#F9F3E5] to-white p-6" data-testid="founder-test-tile">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white border border-[#D4AF37]/60 text-[10px] tracking-[0.22em] uppercase text-[#0A1128] font-semibold">
                <Sparkles className="w-3 h-3 text-[#D4AF37]" /> Founder · $1 smoke test
              </div>
              <h3 className="mt-3 font-serif text-2xl text-[#0A1128] tracking-tight">
                Prove live payments work — for just $1.
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Generates a one-time <b>$1</b> Stripe Checkout URL you can pay yourself, then refund from your Stripe
                dashboard within minutes. If your account is live, this triggers your first-payment celebration email.
              </p>
            </div>
            <Button
              onClick={createFounderTest}
              disabled={founderTestBusy}
              className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white px-6"
              data-testid="founder-test-generate"
            >
              {founderTestBusy ? "Preparing…" : founderTest ? "Generate another" : "Generate $1 checkout"}
            </Button>
          </div>

          {founderTest && (
            <div className="mt-5 rounded-2xl bg-white border border-slate-200 p-4" data-testid="founder-test-result">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] tracking-[0.16em] uppercase font-semibold ${
                    founderTest.mode === "live"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-amber-50 text-amber-700 border border-amber-200"
                  }`}
                  data-testid="founder-test-mode"
                >
                  {founderTest.mode} mode
                </span>
                <span>${(founderTest.amount_cents / 100).toFixed(2)} {founderTest.currency.toUpperCase()}</span>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <input
                  readOnly
                  value={founderTest.checkout_url}
                  className="flex-1 min-w-[240px] px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-xs font-mono text-slate-700"
                  data-testid="founder-test-url"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => {
                    navigator.clipboard.writeText(founderTest.checkout_url);
                    toast.success("URL copied");
                  }}
                  data-testid="founder-test-copy"
                >
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
                <a
                  href={founderTest.checkout_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="founder-test-open"
                >
                  <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open checkout
                  </Button>
                </a>
              </div>
              {founderTest.mode === "test" && (
                <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Preview is on Stripe test mode. Use test card <code className="font-mono">4242 4242 4242 4242</code> · any future date · any CVC.
                  For a <b>real</b> $1 charge, run this on production (guestsmell.com) after redeploying.
                </div>
              )}
            </div>
          )}
        </div>

        <h2 className="mt-12 font-serif text-2xl text-[#0A1128]">Pending Applications ({pending.length})</h2>
        <div className="mt-4 space-y-4">
          {pending.length === 0 && <div className="p-8 rounded-2xl border border-dashed border-slate-300 text-center text-slate-500" data-testid="admin-no-pending">No pending applications.</div>}
          {pending.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-6 md:flex gap-6" data-testid={`admin-cert-${c.id}`}>
              <img src={c.property?.photos?.[0] || "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400"} alt="" className="w-full md:w-40 aspect-video md:aspect-square object-cover rounded-xl" />
              <div className="flex-1 mt-4 md:mt-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs tracking-[0.2em] uppercase px-2 py-1 rounded-full bg-yellow-50 border border-yellow-200 text-yellow-700">Pending · {c.tier}</span>
                </div>
                <h3 className="mt-2 font-serif text-2xl text-[#0A1128]">{c.property?.name}</h3>
                <div className="text-sm text-slate-500">{c.property?.city}, {c.property?.country} · Freshness {c.property?.freshness_score}/100</div>
                <Textarea placeholder="Reviewer notes (optional)" className="mt-3"
                  value={notesMap[c.id] || ""} onChange={(e)=>setNotesMap({...notesMap, [c.id]: e.target.value})}
                  data-testid={`notes-${c.id}`} />
              </div>
              <div className="mt-4 md:mt-0 flex md:flex-col gap-2 min-w-[140px]">
                <Button size="sm" onClick={() => decide(c.id, "approved")} data-testid={`approve-${c.id}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex-1"><Check className="w-4 h-4 mr-1" /> Approve</Button>
                <Button size="sm" variant="outline" onClick={() => decide(c.id, "rejected")} data-testid={`reject-${c.id}`} className="rounded-full flex-1"><X className="w-4 h-4 mr-1" /> Reject</Button>
              </div>
            </div>
          ))}
        </div>

        <h2 className="mt-12 font-serif text-2xl text-[#0A1128]">Recent Decisions</h2>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {decided.slice(0, 10).map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-4">
              <img src={c.property?.photos?.[0] || "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=200"} className="w-16 h-16 object-cover rounded-xl" alt="" />
              <div className="flex-1">
                <div className="font-serif text-lg text-[#0A1128]">{c.property?.name || "—"}</div>
                <div className="text-xs text-slate-500">{c.status.toUpperCase()} · {c.tier}</div>
              </div>
              {c.status === "approved" && <CertifiedBadge tier={c.tier} compact />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
